import React from 'react';
import { MemberRegistrationForm } from '../../types';

interface ConfirmationStepProps {
  formData: MemberRegistrationForm;
}

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({ formData }) => {
  return (
    <div className="text-center py-8">
      {/* Success Icon */}
      <div className="mb-6">
        <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
          <svg
            className="w-12 h-12 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      </div>

      {/* Success Message */}
      <h2 className="text-3xl font-bold text-gray-800 mb-4">Application Submitted Successfully!</h2>
      <p className="text-lg text-gray-600 mb-8">
        Thank you for applying to join the Tumbulgum Fishing Club, {formData.firstName}!
      </p>

      {/* What's Next */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-left mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">What happens next?</h3>
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 w-8 h-8 bg-fishing-blue text-white rounded-full flex items-center justify-center font-semibold mr-3">
              1
            </div>
            <div>
              <h4 className="font-medium text-gray-800">Application Review</h4>
              <p className="text-sm text-gray-600">Our admin team will review your application.</p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="flex-shrink-0 w-8 h-8 bg-fishing-blue text-white rounded-full flex items-center justify-center font-semibold mr-3">
              2
            </div>
            <div>
              <h4 className="font-medium text-gray-800">Payment</h4>
              <p className="text-sm text-gray-600">
                {formData.paymentReceived
                  ? `Your membership fee of $${formData.amountPaid?.toFixed(2)} has been paid online. After successful payment, an administrator will approve your application.`
                  : 'Your membership fee can be paid online via the member portal, or in person on a competition day. An administrator will approve your application once payment is received.'}
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="flex-shrink-0 w-8 h-8 bg-fishing-blue text-white rounded-full flex items-center justify-center font-semibold mr-3">
              3
            </div>
            <div>
              <h4 className="font-medium text-gray-800">Membership Confirmation</h4>
              <p className="text-sm text-gray-600">
                We'll email confirmation and activate your membership once payment is verified.
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="flex-shrink-0 w-8 h-8 bg-fishing-blue text-white rounded-full flex items-center justify-center font-semibold mr-3">
              4
            </div>
            <div>
              <h4 className="font-medium text-gray-800">Welcome to the Club!</h4>
              <p className="text-sm text-gray-600">
                Club merchandise available from time to time, plus full member benefits to
                the best fishing club in Tumbulgum!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Important Information */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
        <p className="text-sm text-amber-800 font-medium">
          📋 An admin will review and approve your application.
        </p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
        <p className="text-sm text-gray-700">
          <strong>Important:</strong> Please check your email (including spam folder) for our
          confirmation message. If you don't receive an email within 24 hours, please contact us at{' '}
          <a href="mailto:fishing@tumbyfc.com" className="text-fishing-blue hover:underline">
            fishing@tumbyfc.com
          </a>
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={() => (window.location.href = '/')}
          className="px-8 py-3 bg-fishing-blue hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          Return to Home
        </button>
        <button
          onClick={() => window.print()}
          className="px-8 py-3 border-2 border-fishing-blue text-fishing-blue hover:bg-blue-50 rounded-lg font-medium transition-colors"
        >
          Print Confirmation
        </button>
      </div>

      {/* Reference Number */}
      <div className="mt-8 text-sm text-gray-500">
        <p>Application Reference: {Date.now().toString(36).toUpperCase()}</p>
      </div>
    </div>
  );
};

export default ConfirmationStep;
